import { useState, useCallback, useMemo } from 'react';
import {
  Typography,
  Button,
  Table,
  Tag,
  Space,
  message,
  Card,
  Spin,
  Row,
  Col,
  Input,
  Grid,
  List
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Reservation, ReservationStatus, ReservationType } from '@/types/residents/reservation';
import { getReservations, addReservation, updateReservation, deleteReservation } from '@/api/residents';
import { useAuth } from '@/contexts/AuthContext';
import ReservationModal from '@/components/features/residents/ReservationModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PAGE_SIZE = 10;

export default function FacilityReservationsPage() {
  const { t } = useTranslation(['reservations', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  const screens = useBreakpoint();

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingReservationId, setDeletingReservationId] = useState<string | null>(null);

  // Fetch reservations
  const { data, isLoading } = useQuery({
    queryKey: ['reservations', page, searchTerm],
    queryFn: () => getReservations(PAGE_SIZE, undefined, firestoreUser ?? undefined, searchTerm, page),
    enabled: !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  // Add reservation mutation
  const addReservationMutation = useMutation({
    mutationFn: (data: Omit<Reservation, 'id'>) => addReservation(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('reservations:createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      setIsModalOpen(false);
    },
    onError: () => {
      message.error(t('reservations:createError'));
    },
  });

  // Update reservation mutation
  const updateReservationMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Reservation> }) =>
      updateReservation(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('reservations:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      setIsModalOpen(false);
    },
    onError: () => {
      message.error(t('reservations:updateError'));
    },
  });

  // Delete reservation mutation
  const deleteReservationMutation = useMutation({
    mutationFn: (id: string) => deleteReservation(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('reservations:deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      setIsDeleteModalOpen(false);
      setDeletingReservationId(null);
    },
    onError: () => {
      message.error(t('reservations:deleteError'));
    },
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingReservation(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((reservation: Reservation) => {
    setEditingReservation(reservation);
    setIsModalOpen(true);
  }, []);

  const handleView = useCallback((reservation: Reservation) => {
    navigate(`/residents/reservations/${reservation.id}`, { state: { reservation } });
  }, [navigate]);

  const handleDelete = useCallback((reservation: Reservation) => {
    setDeletingReservationId(reservation.id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleModalSuccess = useCallback((data: Omit<Reservation, 'id'>) => {
    if (editingReservation) {
      updateReservationMutation.mutate({ id: editingReservation.id, data });
    } else {
      addReservationMutation.mutate(data);
    }
  }, [editingReservation, addReservationMutation, updateReservationMutation]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page when searching
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (deletingReservationId) {
      deleteReservationMutation.mutate(deletingReservationId);
    }
  }, [deletingReservationId, deleteReservationMutation]);

  // Memoized values
  const filteredData = useMemo(() => {
    if (!data?.reservations) return [];
    return data.reservations;
  }, [data?.reservations]);

  const getStatusColor = (status: ReservationStatus) => {
    const colors: Record<ReservationStatus, string> = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      cancelled: 'default',
      completed: 'processing',
    };
    return colors[status];
  };

  const getTypeColor = (type: ReservationType) => {
    const colors: Record<ReservationType, string> = {
      personal: 'blue',
      community: 'green',
      commercial: 'purple',
    };
    return colors[type];
  };

  const renderReservationCard = (reservation: Reservation) => (
    <Card
      key={reservation.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleView(reservation)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(reservation)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(reservation)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={
          <Space>
            <CalendarOutlined />
            <Text strong>{reservation.amenity.name}</Text>
          </Space>
        }
        description={
          <Space direction="vertical" size="small">
            <div>
              <Tag color={getTypeColor(reservation.type)}>
                {t(`reservations:types.${reservation.type}`)}
              </Tag>
              <Tag color={getStatusColor(reservation.status)}>
                {t(`reservations:statuses.${reservation.status}`)}
              </Tag>
            </div>
            <div>
              <UserOutlined /> {reservation.resident?.name || reservation.employee?.name || reservation.vendor?.name || '-'}
            </div>
            <div>
              <ClockCircleOutlined /> {reservation.startTime ? dayjs(reservation.startTime).format('YYYY-MM-DD HH:mm') : '-'}
            </div>
            <div>{t('reservations:attendees')}: {reservation.attendees || '-'}</div>
          </Space>
        }
      />
    </Card>
  );

  const columns = [
    {
      title: t('reservations:amenity'),
      dataIndex: ['amenity', 'name'],
      key: 'amenity',
      render: (name: string) => (
        <Space>
          <CalendarOutlined />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: t('reservations:type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: ReservationType) => (
        <Tag color={getTypeColor(type)}>
          {t(`reservations:types.${type}`)}
        </Tag>
      ),
    },
    {
      title: t('reservations:status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: ReservationStatus) => (
        <Tag color={getStatusColor(status)}>
          {t(`reservations:statuses.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('reservations:reservedBy'),
      dataIndex: 'resident',
      key: 'reservedBy',
      render: (resident: any, record: Reservation) => (
        <Space>
          <UserOutlined />
          {resident?.name || record.employee?.name || record.vendor?.name || '-'}
        </Space>
      ),
    },
    {
      title: t('reservations:startTime'),
      dataIndex: 'startTime',
      key: 'startTime',
      render: (date: Date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a: Reservation, b: Reservation) => 
        (a.startTime?.getTime() ?? 0) - (b.startTime?.getTime() ?? 0),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: t('reservations:attendees'),
      dataIndex: 'attendees',
      key: 'attendees',
      render: (attendees: number) => attendees || '-',
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Reservation) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size="small"
          >
            {t('common:actions.view')}
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            {t('common:actions.edit')}
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            size="small"
            loading={deleteReservationMutation.isPending && deletingReservationId === record.id}
          >
            {t('common:actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>{t('reservations:title')}</Title>
        </Col>
        <Col>
          <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Input
              placeholder={t('reservations:searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              prefix={<CalendarOutlined />}
              style={{ width: screens.xs ? '100%' : 300 }}
            />
            <Button type="primary" onClick={handleAdd} icon={<PlusOutlined />}>
              {t('reservations:add')}
            </Button>
          </Space>
        </Col>
      </Row>

      {screens.xs ? (
        <List
          dataSource={filteredData}
          renderItem={renderReservationCard}
          rowKey="id"
          loading={isLoading}
          grid={{ gutter: 16, xs: 1, sm: 1 }}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: data?.total || 0,
            onChange: (newPage) => setPage(newPage),
            showSizeChanger: false,
          }}
          scroll={{ x: 'max-content' }}
        />
      )}

      {/* Modals */}
      <ReservationModal
        open={isModalOpen}
        reservation={editingReservation}
        onSuccess={handleModalSuccess}
        onCancel={() => setIsModalOpen(false)}
      />

      <DeleteConfirmationModal
        visible={isDeleteModalOpen}
        title={t('reservations:deleteConfirmTitle')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setDeletingReservationId(null);
        }}
      />
    </div>
  );
} 